import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './en';
import pl from './pl';

const STORAGE_KEY = 'securepaper_lang';
const dictionaries = { en, pl };

// Resolve the initial language: saved preference first, then browser language.
const resolveInitialLang = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'pl') {
      return stored;
    }
  } catch {
    // storage unavailable - fall through to browser detection
  }
  try {
    if (typeof navigator !== 'undefined' && (navigator.language || '').toLowerCase().startsWith('pl')) {
      return 'pl';
    }
  } catch {
    // ignore
  }
  return 'en';
};

// Look up a dot-separated key ("nav.login") in a nested dictionary object.
const lookup = (dict, key) =>
  key.split('.').reduce(
    (node, part) => (node && typeof node === 'object' ? node[part] : undefined),
    dict
  );

// Replace {placeholders} with values from params.
const interpolate = (template, params) => {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match
  );
};

// Translate a key in a given language, falling back to English, then the key itself.
export const translate = (lang, key, params) => {
  let value = lookup(dictionaries[lang] || en, key);
  if (typeof value !== 'string') {
    value = lookup(en, key);
  }
  if (typeof value !== 'string') {
    return key;
  }
  return interpolate(value, params);
};

const makeContextValue = (lang, setLang) => ({
  lang,
  setLang,
  t: (key, params) => translate(lang, key, params),
});

// Default value lets components render sensibly even outside a provider
// (e.g. anything mounted outside the main tree): t() works, setLang is a no-op.
const LanguageContext = createContext(makeContextValue(resolveInitialLang(), () => {}));

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(resolveInitialLang);

  // Keep <html lang> in sync with the active UI language
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    const value = next === 'pl' ? 'pl' : 'en';
    setLangState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // ignore storage errors (private mode, disabled storage, ...)
    }
  }, []);

  const value = useMemo(() => makeContextValue(lang, setLang), [lang, setLang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => useContext(LanguageContext);

export default LanguageContext;
